import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import { usePageSEO } from "../hooks/usePageSEO";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function Unsubscribe() {
  usePageSEO({
    title: "Abmelden — Alphalane Trading Systems",
    description: "Vom Newsletter abmelden.",
    canonical: "https://alphalane-tradingsystems.com/unsubscribe",
    noIndex: true,
  });

  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";

  const [state, setState] = useState({ status: "loading", message: "" });

  useEffect(() => {
    if (!email || !token) {
      setState({ status: "error", message: "Ungültiger Abmelde-Link. Bitte nutze den Link aus deiner letzten E-Mail." });
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/unsubscribe`, { params: { email, token } });
        setState({
          status: "success",
          message: `Du wurdest erfolgreich vom Newsletter abgemeldet. ${res.data.updated > 0 ? "Bestätigung gespeichert." : ""}`,
        });
      } catch (err) {
        setState({
          status: "error",
          message: err?.response?.data?.detail || "Abmeldung fehlgeschlagen. Bitte kontaktiere uns direkt: kontakt@moonshine-solutions.com",
        });
      }
    })();
  }, [email, token]);

  return (
    <div className="min-h-screen bg-[var(--al-bg)] flex flex-col">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display">
              <span className="font-semibold text-base"><span className="al-grad-text">ALPHALANE</span><span className="text-slate-500 ml-2">AI</span></span>
            </div>
          </Link>
          <Link to="/" data-testid="unsub-back" className="al-cta-ghost !py-2 !px-4 text-sm">
            Zur Website
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="al-card p-9 sm:p-12 max-w-xl w-full text-center" data-testid="unsub-card">
          {state.status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 text-[var(--al-purple)] mx-auto animate-spin" />
              <h1 className="mt-6 font-display font-bold text-2xl sm:text-3xl">Wird verarbeitet…</h1>
              <p className="mt-3 text-sm text-slate-600">Wir entfernen deine Adresse vom Verteiler.</p>
            </>
          )}

          {state.status === "success" && (
            <>
              <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h1 className="mt-6 font-display font-bold text-2xl sm:text-3xl" data-testid="unsub-success">
                Abgemeldet.
              </h1>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                Wir senden dir keine weiteren Newsletter-E-Mails mehr. Falls du eine konkrete Anfrage zu unserem
                Trading-System hast, kannst du dich jederzeit direkt bei uns melden.
              </p>
              {email && (
                <p className="mt-5 text-xs text-slate-400 font-mono-tab">
                  Abgemeldet: {email}
                </p>
              )}
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/" className="al-cta-ghost text-sm">Zur Website</Link>
                <a href="mailto:kontakt@moonshine-solutions.com" className="al-cta text-sm">Direkt kontaktieren</a>
              </div>
            </>
          )}

          {state.status === "error" && (
            <>
              <div className="h-16 w-16 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
                <AlertCircle className="h-9 w-9 text-rose-600" />
              </div>
              <h1 className="mt-6 font-display font-bold text-2xl sm:text-3xl" data-testid="unsub-error">
                Hoppla — Abmeldung fehlgeschlagen.
              </h1>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                {state.message}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <a href="mailto:kontakt@moonshine-solutions.com?subject=Vom Newsletter abmelden" className="al-cta text-sm">
                  Per E-Mail abmelden
                </a>
                <Link to="/" className="al-cta-ghost text-sm">Zur Website</Link>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-[var(--al-line)] bg-white py-6 text-center text-xs text-slate-500">
        © Alphalane Trading Systems · Moonshine Solutions GmbH · Schmitten FR, Schweiz
      </footer>
    </div>
  );
}
